import {inject, Injectable, signal} from "@angular/core"
import {FormBuilder, Validators} from "@angular/forms"
import {Annotations, YAxisName} from "plotly.js"
import {BehaviorSubject, firstValueFrom} from "rxjs"
import {Counter} from "src/app/shared/counter"
import {Measurement, Project, BasicObjectAndProject, VideObject} from "../../api/api-types"
import {MeasureType} from "../../api/measure-type"
import {VideDataService} from "../../api/vide-data.service"
import {LogContainer} from "../../log-list/log-container"
import {idDefined, isDefined} from "../../shared/vide-helper"

/** Measurements as found in DiverOffice files */
export interface DiverMeasurement {
    dateTime: string
    value: number
    temp: number
}

export const DIVER_DATA_CODES: MeasureType["code"][] = [
    "mt_diver_uncompensated",
    "mt_diver_unreferenced",
    "mt_diver_pressure",
    "mt_level_logger",
] as const

export interface DiverFileData {
    readonly measurements: readonly DiverMeasurement[]
    readonly serial: string
    readonly type: MeasureType
    readonly head: readonly string[]
    readonly filename: string
}

export type DiverDatum = {
    value: number
    time_t: number
    dateTime: string
    id?: number
}

export type ClickedPoint = {
    x: string
    y: number
    yref: YAxisName
}
export type PlotLimits = {begin?: ClickedPoint; readonly end?: ClickedPoint}

@Injectable({
    providedIn: "root",
})
export class DiverService {
    private readonly dataService = inject(VideDataService)
    private readonly fb = inject(FormBuilder)

    readonly form = this.fb.nonNullable.group({
        saveInterval: [1, Validators.required],
    })
    static readonly plotColors = {
        // temperature: "#000", // too black
        temperature: "#555", // ljussvart

        // D3 colors
        pressure: "#FF7F0E",
        result: "#2CA02C",
        uncompensated: "#D62728",
        unreferenced: "#1F77B4",
        references: "#9467BD",
        discarded: "#777",

        // // The unused ones
        //         a: "#8C564B",
        //         a: "#E377C2",
        //         a: "#7F7F7F",
        //         a: "#BCBD22",
        //         a: "#17BECF",
    } as const

    private readonly counter = new Counter()
    // Stuff for coordinating the components
    private readonly _selectedObject =
        new BehaviorSubject<BasicObjectAndProject | null>(null)
    private readonly _fileData = new BehaviorSubject<DiverFileData | null>(null)
    private readonly _pressureData = new BehaviorSubject<readonly DiverDatum[]>(
        [],
    )
    private readonly _localCompensatedData = new BehaviorSubject<null | {
        pressure: readonly DiverDatum[]
        unreferenced: readonly DiverDatum[]
        uncompensated: readonly DiverDatum[]
    }>(null)
    // corresponding observables
    readonly selectedObject$ = this._selectedObject.asObservable()
    readonly fileData$ = this._fileData.asObservable()
    readonly pressureData$ = this._pressureData.asObservable()
    readonly localCompensatedData$ = this._localCompensatedData.asObservable()

    readonly logs = new LogContainer()
    readonly nrUncompensated = signal<
        undefined | {database: number | undefined; file: number | undefined}
    >(undefined)
    readonly nrUnreferenced = signal<
        | undefined
        | {
              database: number | undefined
              file: number | undefined
              local: number | undefined
          }
    >(undefined)

    async createNewObject(project: Project, name: string) {
        // const p = await firstValueFrom(this.dataService.projectNotNull$)
        const objectType = await firstValueFrom(
            this.dataService.objectTypeWell$,
        )
        const objectStatus = await firstValueFrom(
            this.dataService.objectStatusActive$,
        )
        const request = this.dataService.createSingleObject(project, {
            name,
            object_status: objectStatus,
            object_type: objectType,
            original_information: true, // ???
        })
        const result = await firstValueFrom(request)
        this.logs.add(result, `Create object ${name}`)
        if (result.success) {
            this.dataService.reloadProjectData()
        }
        return result
    }

    /**
     * Save the data to the object, and on success, reload project data.
     * Row with id are updates, rows without are created, so this may produce two http requests.
     *
     * @param data The clipped data
     * @param options
     */
    async saveToObject(
        data: readonly {dateTime: string; value: number; id?: number}[],
        options: {
            object: BasicObjectAndProject
            measureType: MeasureType
            update: boolean
        },
    ) {
        const dataStatus = await firstValueFrom(
            this.dataService.dataStatusStandard$,
        )
        const msg = `Save ${options.measureType.getDescription()} measurements to ${options.object.object.name} `

        let resUpdate
        let resCreate

        const updateRows = data.filter(idDefined)
        if (updateRows.length > 0) {
            resUpdate = await firstValueFrom(
                this.dataService.updateMeasurements(
                    options.object.project,
                    updateRows.map((x) => ({
                        data_status: dataStatus,
                        error_code: null,
                        id: x.id,
                        measure_type: options.measureType,
                        measured_value: null,
                        object_id: options.object.object.id,
                        resulting_value: x.value,
                    })),
                ),
            )
            this.logs.add(resUpdate, msg)
            if (!resUpdate.success) {
                // Early exit on failure
                return resUpdate
            }
        }

        const createRows = data.filter((x) => !idDefined(x))
        if (createRows.length > 0) {
            resCreate = await firstValueFrom(
                this.dataService.createMeasurements(
                    options.object.project,
                    {
                        dataStatus: dataStatus,
                        textCode: null,
                        measureType: options.measureType,
                        object: options.object.object,
                    },
                    createRows.map((x) => ({
                        measuretime: x.dateTime,
                        resultingValue: x.value,
                    })),
                ),
            )
            this.logs.add(resCreate, msg)

            if (!resCreate.success) {
                // Create failed, return the error.
                return resCreate
            }
        }
        const res = resUpdate || resCreate
        if (!res) {
            throw Error("Nothing to save")
        }
        // Something was done successfully
        this.dataService.reloadProjectData()
        return res
    }

    async createDiverNote(
        object: BasicObjectAndProject,
        arg: {
            measureTypeDescription: string
            first_date: string
            serial_number?: string
            referenceComment?: string
        },
    ) {
        const comments = [
            `Saved ${arg.measureTypeDescription}`,
            `First value ${arg.first_date}`,
        ]
        if (isDefined(arg.serial_number))
            comments.push(`Diver serial number ${arg.serial_number}`)
        if (isDefined(arg.referenceComment))
            comments.push(`Reference measurement ${arg.referenceComment}`)
        const obs = this.dataService.createNote(
            object.project,
            object.object,
            comments.join("\n"),
        )
        const r = await this.counter.count2(obs)
        this.logs.add(r, "Create note")
        if (r.success) {
            this.dataService.reloadProjectData()
        }
        return r
    }

    async deleteMeasurements(
        selected: BasicObjectAndProject,
        measurements: readonly Measurement[],
    ) {
        // const project = await firstValueFrom(this.dataService.projectNotNull$)
        const res = await firstValueFrom(
            this.dataService.deleteMeasurements(
                selected.project,
                selected.object,
                measurements.map((x) => x.id),
            ),
        )
        this.logs.add(res, `Delete measurements`)
        if (res.success) {
            this.dataService.reloadProjectData()
        }
        return res
    }

    setObject(object: BasicObjectAndProject | null) {
        this._selectedObject.next(object)
    }

    setFileData(data: DiverFileData | null) {
        this._fileData.next(data)
    }

    setPressureData(
        data: null | {
            data: readonly DiverDatum[]
            unitName: string
            object: VideObject
        },
    ) {
        this._pressureData.next(data?.data ?? [])
    }

    setLocalCompensatedData(
        data: null | {
            pressure: readonly DiverDatum[]
            uncompensated: readonly DiverDatum[]
            unreferenced: readonly DiverDatum[]
            skipped: readonly DiverDatum[]
        },
    ) {
        this._localCompensatedData.next(data)
    }

    /**
     * Parses a file as from DiverOffice. Expects data to be in mH2O and degrees Celsius.
     *
     * @param filename
     * @param text
     */
    async parseDiverOfficeData(filename: string, text: string) {
        const utility = await firstValueFrom(this.dataService.utility$)
        const rows = text.split("\r\n")
        const length = rows.length
        let idx = 0
        while (idx < length && rows.at(idx) !== "[Logger settings]") {
            ++idx
        }
        let match
        while (
            idx < length &&
            (match = rows.at(idx)?.match(/ {2}Serial number +=(.+)/)) === null
        ) {
            ++idx
        }
        if (!match) {
            throw new Error("No serial number found")
        }
        const serial = match.at(1)!
        const headerUncompensatedM = "Date/time;Pressure[mH2O];Temperature[°C]"
        const headerUncompensatedCm =
            "Date/time;Pressure[cmH2O];Temperature[°C]"
        const headerUnrefCm = "Date/time;Water head[cm];Temperature[°C]"
        const headerUnrefM = "Date/time;Water head[m];Temperature[°C]"

        let current
        while (
            idx < length &&
            (current = rows.at(idx)) !== undefined &&
            !current.startsWith("Date/time;")
        ) {
            ++idx
        }
        if (idx === length) {
            // Found no data header, maybe the format is different, and we should handle this too?
            throw new Error("No header row found, maybe the format is unknown")
        }
        let factor
        let type: MeasureType
        switch (current) {
            case headerUnrefM:
                // 'Unreferenced'
                type = utility.getMeasureTypeByCode(
                    "mt_diver_unreferenced",
                )
                factor = 1.0
                break
            case headerUncompensatedM:
                // type = 'Uncompensated'
                type = utility.getMeasureTypeByCode(
                    "mt_diver_uncompensated",
                )
                factor = 1.0
                break
            case headerUnrefCm:
                // type = 'Unreferenced'
                type = utility.getMeasureTypeByCode(
                    "mt_diver_unreferenced",
                )
                factor = 0.01
                break
            case headerUncompensatedCm:
                // type = 'Uncompensated'
                type = utility.getMeasureTypeByCode(
                    "mt_diver_uncompensated",
                )
                factor = 0.01
                break
            default:
                throw new Error(`Unknown header row ${current}`)
        }
        const head = rows.splice(0, idx + 1)

        const data = []
        for (const row of rows) {
            if (row === "END OF DATA FILE OF DATALOGGER FOR WINDOWS") break
            const [dateTime, value, temp] = row.split(";")
            if (!isDefined(dateTime) || !isDefined(value) || !isDefined(temp))
                throw new Error(`Cannot parse row ${row}`)
            const match = dateTime.match(
                /^(?<year>\d{4})\/(?<month>\d{2})\/(?<date>\d{2}) (?<time>\d{2}:\d{2}:\d{2})/,
            )
            if (!match?.groups)
                throw new Error(`Cannot parse date-time ${dateTime}`)
            const year = match.groups["year"]!
            const month = match.groups["month"]!
            const date = match.groups["date"]!
            const time = match.groups["time"]!
            data.push({
                dateTime: `${year}-${month}-${date} ${time}`,
                value: factor * parseFloatComma(value),
                temp: parseFloatComma(temp),
            })
        }

        const newVar: DiverFileData = {
            serial,
            measurements: data,
            head,
            type,
            filename: filename,
        }
        return newVar
    }
}

export function readAsText(file: File) {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (_ev) => {
            const result = reader.result
            if (typeof result === "string") {
                resolve(result)
            } else {
                reject(new Error("Type error"))
            }
        }
        reader.readAsText(file, "ISO-8859-1")
    })
}

export function limitsToAnnotations(limits: PlotLimits) {
    const annotations: Partial<Annotations>[] = []
    if (limits.begin) {
        annotations.push({
            text: "begin",
            x: limits.begin.x,
            y: limits.begin.y,
            yref: limits.begin.yref,
        })
    }
    if (limits.end) {
        annotations.push({
            text: "end",
            x: limits.end.x,
            y: limits.end.y,
            yref: limits.end.yref,
        })
    }
    return annotations
}

function parseFloatComma(str: string) {
    switch (countChar(",", str)) {
        case 1:
            return parseFloat(str.replace(",", "."))
        case 0:
            return parseFloat(str)
        default:
            throw new Error("String has more than one comma")
    }
}

function countChar(char: string, string: string) {
    let finds = 0
    for (let i = 0; i < string.length; i++) {
        if (char === string.charAt(i)) finds++
    }
    return finds
}

export function getLimitFilter<T extends Pick<DiverMeasurement, "dateTime">>(
    limits: PlotLimits,
) {
    const begin = limits.begin?.x
    const end = limits.end?.x
    const filters = [
        isDefined(begin) ? (x: T) => begin <= x.dateTime : undefined,
        isDefined(end) ? (x: T) => x.dateTime <= end : undefined,
    ].filter(isDefined)
    return (x: T) => filters.every((f) => f(x))
}
export function interpolate(
    a: {x: number; y: number},
    b: {x: number; y: number},
    x: number,
) {
    const deltaX = b.x - a.x
    const deltaY = b.y - a.y
    const delta = x - a.x

    return a.y + (delta * deltaY) / deltaX
}
